namespace SaAlertUtils {
    export interface AlertConfirmationOptions {
        showCloseButton?: boolean;
        disableConfirmUntilInput?: boolean;
    }

    export enum SweetAlertClassName {
        SweetAlertContainerMedium = "saSweetAlert saSweetAlertMd",
        SweetAlertContainerLarge = "saSweetAlert saSweetAlertLg",
        SweetAlertHtmlContainerLabel = "swal2-html-container-label",
        SweetAlertInputLabel = "swal2-input-label",
    }

    export const fallbackErrorMessage: string =
        "An error occurred while processing the request. Please contact support if this problem continues.";

    export const defaultOptions: SweetAlertOptions = {
        color: "var(--saColorBrandBlack)",
        showCancelButton: false,
        confirmButtonText: "Ok",
        buttonsStyling: false,
        showCloseButton: false,
        customClass: {
            container: "saSweetAlert saSweetAlertLg",
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-secondary",
        },
    };

    export async function fire(
        options: SweetAlertOptions,
    ): Promise<SweetAlertResult> {
        return await globalThis.Swal.fire(options);
    }

    export async function defaultFire(
        title: string,
        text: string | string[] | null | undefined,
        options: SweetAlertOptions = defaultOptions,
    ): Promise<SweetAlertResult> {
        return await fire({
            ...options,
            title: title,
            html: formatAlertTextAsHtml(text),
        });
    }

    export async function defaultFireFromResponse(
        title: string,
        xhr: JQuery.jqXHR,
    ): Promise<string[] | null> {
        const response = xhr.responseJSON as ApiResponse;
        if (response?.errors && response.errors.length > 0) {
            await defaultFire(title, response?.errors);
            return response.errors;
        }

        const stringResponse = xhr.responseText;
        console.error("Unexpected API error response:", stringResponse);

        await defaultFire(title, fallbackErrorMessage);
        return null;
    }

    export async function fireAlertConfirmation(
        title: string,
        text: string,
        confirmBtnText: string,
        cancelBtnText: string | undefined,
        onConfirm?: (result: SweetAlertResult) => void | Promise<void>,
        confirmationOptions?: AlertConfirmationOptions,
    ): Promise<SweetAlertResult> {
        const showCancelBtn = !!cancelBtnText && cancelBtnText.trim() !== "";
        const options = { ...SaAlertUtils.defaultOptions };

        options.showCancelButton = showCancelBtn;
        options.cancelButtonText = cancelBtnText;
        options.confirmButtonText = confirmBtnText;
        options.returnFocus = false;
        options.showCloseButton = confirmationOptions?.showCloseButton ?? false;

        const result = await defaultFire(title, text, options);

        if (result !== null && onConfirm) {
            await onConfirm(result);
        }

        return result;
    }

    export async function fireAlertConfirmationWithTextInput(
        title: string,
        text: string,
        confirmBtnText: string,
        cancelBtnText: string | undefined,
        inputLabel: string,
        inputValidator?: (value: string) => string | void,
        onConfirm?: (result: SweetAlertResult) => void | Promise<void>,
    ): Promise<SweetAlertResult> {
        const showCancelBtn = !!cancelBtnText && cancelBtnText.trim() !== "";
        const options = { ...SaAlertUtils.defaultOptions };

        options.showCancelButton = showCancelBtn;
        options.cancelButtonText = cancelBtnText;
        options.confirmButtonText = confirmBtnText;
        options.returnFocus = false;
        options.input = "text";
        options.inputLabel = inputLabel;
        if (inputValidator) {
            options.inputValidator = inputValidator;
        }

        const result = await defaultFire(title, text, options);

        if (result !== null && onConfirm) {
            await onConfirm(result);
        }

        return result;
    }

    export async function fireAlertConfirmationWithSelectInput(
        title: string,
        text: string,
        confirmBtnText: string,
        cancelBtnText: string | undefined,
        inputLabel: string,
        inputOptions: Record<string, string>,
        inputValidator?: (value: string) => string | void,
        onConfirm?: (result: SweetAlertResult) => void | Promise<void>,
        confirmationOptions?: AlertConfirmationOptions,
    ): Promise<SweetAlertResult> {
        const showCancelBtn = !!cancelBtnText && cancelBtnText.trim() !== "";
        const options = { ...SaAlertUtils.defaultOptions };

        options.showCancelButton = showCancelBtn;
        options.cancelButtonText = cancelBtnText;
        options.confirmButtonText = confirmBtnText;
        options.returnFocus = false;
        options.input = "select";
        options.inputLabel = inputLabel;
        options.inputOptions = inputOptions;
        options.showCloseButton = confirmationOptions?.showCloseButton ?? false;
        if (confirmationOptions?.disableConfirmUntilInput) {
            options.didOpen = () => {
                const input = Swal.getInput() as HTMLSelectElement | null;
                const confirmButton = Swal.getConfirmButton();
                if (!input || !confirmButton) {
                    return;
                }

                const setConfirmButtonState = () => {
                    confirmButton.disabled = input.value.trim().length === 0;
                };

                setConfirmButtonState();
                input.addEventListener("change", setConfirmButtonState);
            };
        }
        if (inputValidator) {
            options.inputValidator = inputValidator;
        }

        const result = await defaultFire(title, text, options);

        if (result !== null && onConfirm) {
            await onConfirm(result);
        }

        return result;
    }

    export async function fireFromCaughtError(data: unknown): Promise<void> {
        if (SaAjaxUtils.isJqXHR(data)) {
            await SaAlertUtils.defaultFireFromResponse("Update Failed", data);
        } else if (
            data !== null &&
            typeof data === "object" &&
            "responseText" in data &&
            "message" in data &&
            (data.responseText || data.message)
        ) {
            const errorMessage =
                String(data.responseText) ||
                String(data.message) ||
                "An unknown error occurred.";
            await SaAlertUtils.defaultFire("Update Failed", errorMessage);
        } else {
            await SaAlertUtils.defaultFire(
                "Update Failed",
                "There is an error with updating your information",
            );
        }
    }

    export function formatAlertTextAsHtml(
        text: string | string[] | null | undefined,
    ): string | undefined {
        if (text === null || text === undefined) {
            return undefined;
        }
        if (Array.isArray(text)) {
            return formatAlertTextArrayAsHtml(text);
        }
        return formatAlertTextStringAsHtml(text);
    }

    function formatAlertTextStringAsHtml(text: string): string {
        return formatAlertTextArrayAsHtml(
            text.replaceAll("\r\n", "\n").split("\n"),
        );
    }

    function formatAlertTextArrayAsHtml(textArray: string[]): string {
        const html = [`<div class="d-flex flex-column bd-highlight">`];
        textArray.forEach((text) => {
            html.push(
                `<div class="p-2 bd-highlight">${SaParsingUtils.escapeHtml(text)}</div>`,
            );
        });
        html.push(`</div>`);

        return html.join("");
    }
}

globalThis.SaAlertUtils = SaAlertUtils;
